import { COST_CATEGORIES, COST_CATEGORY_LABEL, formatUsd } from '@orgus/protocol';
import { BookOpen, Download, FileSpreadsheet, Rows3 } from '@orgus/ui/icons';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Link } from 'react-router';
import { BUTTON_SIZE, BUTTON_VARIANT, Button, Panel, Tooltip, cn, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@orgus/ui';
import { useOrgus, useProjects } from '../../lib/store.ts';
import { exportUrl } from '../costs/useCosts.ts';
import { CATEGORY_COLOR, financeExportUrl, monthLabel, recentMonths } from './model.ts';
import { ReportGate, ReportSkeleton, ScopeBar, useScope } from './Scope.tsx';
import { useFinance, useFinanceReport } from './useFinance.ts';

function lastDayOf(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return `${month}-${String(new Date(y!, m!, 0).getDate()).padStart(2, '0')}`;
}

function DownloadCard({ icon, title, body, href, disabledWhy }: { icon: ReactNode; title: string; body: ReactNode; href: string; disabledWhy?: string }) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-4 hairline">
      <span className="flex items-center gap-2 text-body font-medium text-fg [&_svg]:size-4 [&_svg]:text-fg-subtle">
        {icon}
        {title}
      </span>
      <p className="flex-1 text-caption text-fg-subtle">{body}</p>
      {disabledWhy ? (
        <Tooltip content={disabledWhy}>
          <span tabIndex={0} className="self-start">
            <Button size="sm" variant="secondary" icon={<Download />} disabled>
              Download CSV
            </Button>
          </span>
        </Tooltip>
      ) : (
        <a href={href} download className={cn(BUTTON_VARIANT.secondary, BUTTON_SIZE.sm, 'inline-flex items-center self-start font-medium')}>
          <Download aria-hidden />
          Download CSV
        </a>
      )}
    </div>
  );
}

/** CSV and accounting exports per project and month, for chargeback or the accountant. */
export function ExportsView() {
  const projects = useProjects();
  const finance = useFinance();
  const mock = useOrgus((s) => s.mode === 'mock');
  const months = recentMonths(Date.now());
  const [month, setMonth] = useState(months[0]!);
  // The project comes from the shared scope (?project), like every Spend tab; the month is this tab's own period.
  const scope = useScope();
  const projectId = scope.projectId ?? '';
  const result = useFinanceReport({ from: `${month}-01`, to: lastDayOf(month), projectId: projectId || null });
  const projectName = projectId ? (projects.find((p) => p.id === projectId)?.name ?? projectId) : 'every project';
  const why = mock ? 'Exports need a live hub; demo data has no ledger' : undefined;
  return (
    <>
      <ScopeBar scope={scope} result={result} hideAccount month={{ value: month, options: months.map((m) => ({ value: m, label: monthLabel(m) })), onChange: setMonth }} />
      <div className="flex flex-col gap-5">
        <div className="grid gap-3 lg:grid-cols-3">
          <DownloadCard
            icon={<Rows3 />}
            title="Every cost line"
            body="A row per day, category, project and source"
            href={financeExportUrl({ month, projectId: projectId || null, format: 'lines' })}
            disabledWhy={why}
          />
          <DownloadCard
            icon={<BookOpen />}
            title="Accounting journal"
            body="Balanced, imports into QuickBooks and Xero"
            href={financeExportUrl({ month, projectId: projectId || null, format: 'journal' })}
            disabledWhy={why}
          />
          <DownloadCard
            icon={<FileSpreadsheet />}
            title="Token usage rows"
            body="Every usage row, all projects"
            href={exportUrl(month)}
            disabledWhy={why}
          />
        </div>
        <ReportGate result={result} skeleton={<ReportSkeleton tiles={0} />}>
          {(r) => (
            <Panel title={`${monthLabel(month)}, ${projectName}`} sub={formatUsd(r.total)} icon={<BookOpen />} flush>
              <Table aria-label={`Spend by category, ${monthLabel(month)}`}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead className="hidden md:table-cell">Account</TableHead>
                    <TableHead numeric>Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {COST_CATEGORIES.map((c) => (
                    <TableRow key={c}>
                      <TableCell primary>
                        <span className="flex min-w-0 items-center gap-2">
                          <span aria-hidden className="size-2 shrink-0 rounded-full" style={{ background: CATEGORY_COLOR[c] }} />
                          <span className="flex min-w-0 flex-col">
                            <span className="truncate">{COST_CATEGORY_LABEL[c]}</span>
                            {/* On a phone the account rides under the category, so Amount keeps its column. */}
                            {finance?.settings.accounts[c] && <span className="truncate text-caption text-fg-subtle md:hidden">{finance.settings.accounts[c]}</span>}
                          </span>
                        </span>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{finance?.settings.accounts[c] ?? ''}</TableCell>
                      <TableCell numeric className="whitespace-nowrap text-fg">
                        {formatUsd(r.byCategory[c])}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="border-t border-border-subtle px-4 py-2 text-caption text-fg-subtle">
                {r.unallocated.length ? `${formatUsd(r.unallocated.reduce((a, u) => a + u.amount, 0))} with no project exports as Unallocated. ` : ''}
                <Link to="/settings/finance" className="text-fg-muted underline-offset-2 hover:underline">
                  Edit accounts
                </Link>
              </p>
            </Panel>
          )}
        </ReportGate>
      </div>
    </>
  );
}
